import React from "react";
import * as ReactDOM from "react-dom";
import { Icon, CompanyLogo } from "./registry";
import { computeQuoteView, GRADE_LABELS, fenceSpecFrom, quoteBom } from "./quote";
import { buildStepsFor, bomStepCounts, stepQtyFor } from "./lib/buildsteps";
import { stepMinutes, totalMinutes, fmtDur, ratesWithDefaults } from "./lib/steptime";
import { hasProfile, measuredGrade, runGradePct, cumSeries } from "./lib/grade";
import { verticalOf } from "./lib/verticals";
import { ConcreteCrewSheet } from "./concretecrew";   // a concrete quote's sheet (plan §5.3); this file stays the fence sheet
/* ============================================================
   Fence Works — Crew Build Sheet
   The one-tap printable sheet the contractor hands the crew:
   specs, per-run lengths + measured grades, the bill of
   materials to load on the truck, and the ordered build
   sequence (checkbox per step) straight from the same data the
   3D viewer animates and the invoice bills. Pure data + DOM —
   never imports fencebuild, so three.js stays out of this path.
   ============================================================ */

const fmtPct = (v: any) => (Math.round(v * 10) / 10).toFixed(Math.round(v * 10) % 10 ? 1 : 0);

/* step-count noun for the quantity badge ("44 posts") */
const QTY_NOUNS: any = { posts: "posts", sleeves: "sleeves", rails: "rails", boards: "boards", caps: "caps", screws: "screws", fittings: "fittings", footings: "footings", fabric: "mesh", ties: "ties", gates: "gates" };
function qtyLabel(step: any, counts: any) {
  const qty = stepQtyFor(step, counts);
  if (qty == null) return null;
  const k = (step.qk || []).find((x: any) => counts[x] != null);
  let noun = QTY_NOUNS[k] || "pcs";
  if (qty === 1 && /s$/.test(noun)) noun = noun.slice(0, -1);
  return `${qty} ${noun}`;
}

/* grade cell for one run — measured profile (real %, drop, tier) beats the
   contractor's categorical pick; stepped/sloped runs get a visible flag */
function gradeInfo(side: any) {
  if (hasProfile(side)) {
    const g = measuredGrade(side.profile, side.lf || 0);
    const cums = cumSeries(side.profile);
    const net = cums.length ? cums[cums.length - 1].elevIn : 0;
    return {
      main: `${fmtPct(g.pct)}% measured`,
      sub: `Δ ${net < 0 ? "−" : "+"}${(+Math.abs(net).toFixed(1))}″ over ${Math.round(side.lf || 0)}′ · ${g.label}`,
      flag: g.tier === "steep" ? "STEPPED" : g.tier === "moderate" ? "SLOPED" : null,
    };
  }
  const gr = String(side.grade || "level");
  return {
    main: GRADE_LABELS[gr] || "Level",
    sub: null,
    flag: /^step/.test(gr) ? "STEPPED" : /^slope/.test(gr) ? "SLOPED" : null,
  };
}

/* derive one section's ordered build steps + the BOM per-class counts they
   resolve against — the single source both the rendered sheet AND the time
   estimate read, so the displayed quantities and the minutes always agree */
function sectionSteps({ prod, calc, inputs }: any) {
  const spec = fenceSpecFrom(prod);
  const sides = inputs && inputs.parcel && Array.isArray(inputs.parcel.sides)
    ? inputs.parcel.sides.filter((s: any) => s.selected) : null;
  const surveyRuns = (sides || []).filter((s: any) => hasProfile(s)).map((s: any) => ({ name: s.name, pct: runGradePct(s.profile, s.lf || 0) }));
  const steps = buildStepsFor({ ...spec, gates: calc.gates | 0, surveyRuns });
  // step quantities: the BOM's per-class counts (same mapping the 3D viewer
  // uses), with the engine's post/gate counts on top — invoice numbers win
  const counts = bomStepCounts(quoteBom(calc, prod), { posts: calc.posts, gates: calc.gates });
  // this section's linear feet — feeds the flat steps' optional size scaling
  const feet = (sides && sides.length) ? sides.reduce((a: number, s: any) => a + (+s.lf || 0), 0) : (+(inputs && inputs.linearFeet) || +calc.LF || 0);
  return { spec, sides, steps, counts, feet };
}

/* one fence's spec strip + runs + build sequence (multi-fence quotes render
   one section per fence; single-fence quotes render exactly one).
   Rates resolve per-product: global Settings.stepRates ← this product's sparse
   overrides; the section's feet thread into the flat steps' size scaling. */
function SheetSection({ name, prod, calc, inputs, only, showTimes, rates }: any) {
  const { spec, sides, steps, counts, feet } = sectionSteps({ prod, calc, inputs });
  const secRates = ratesWithDefaults(rates, prod && prod.stepRates);
  const runs = sides && sides.length
    ? sides.map((s: any) => ({ label: `Side ${s.i + 1} (${s.name}${s.suffix ? " — " + s.suffix : ""})`, lf: s.lf || 0, ...gradeInfo(s) }))
    : [{ label: "Fence line", lf: (inputs && inputs.linearFeet) || calc.LF || 0, main: "Level", sub: null, flag: null }];
  const graded = runs.some((r: any) => r.flag);
  const sectionMin = showTimes ? totalMinutes(steps, counts, secRates, feet) : 0;
  return (
    <div className="cs-section">
      {/* spec block */}
      <div className="cs-spec">
        <div className="cs-spec-name">
          {name ? <span className="cs-fence-tag">{name}</span> : null}
          <b>{prod.name}</b>
          <span className="cs-spec-sub">{[spec.color, spec.style].filter(Boolean).join(" · ") || spec.category}</span>
        </div>
        <div className="cs-spec-cells">
          {[
            ["Height", `${spec.heightFt}′`],
            ["Panel", `${spec.panelFt}′`],
            ["Total", `${Math.round(calc.LF)} ft`],
            ["Posts", calc.posts],
            ["Panels", calc.panels],
            ["Gates", calc.gates | 0],
          ].map(([lb, v]: any) => (
            <div key={lb} className="cs-cell"><span className="cs-cell-v tnum">{v}</span><span className="cs-cell-l">{lb}</span></div>
          ))}
        </div>
      </div>

      {/* per-run breakdown */}
      <div className="cs-kicker">Runs{only ? "" : ` — ${name}`}</div>
      <table className="cs-table">
        <thead><tr><th>Run</th><th style={{ textAlign: "right" }}>Length</th><th style={{ textAlign: "right" }}>Grade</th></tr></thead>
        <tbody>
          {runs.map((r: any, i: number) => (
            <tr key={i}>
              <td style={{ fontWeight: 600 }}>{r.label}</td>
              <td className="tnum" style={{ textAlign: "right" }}>{Math.round(r.lf)} ft</td>
              <td style={{ textAlign: "right" }}>
                {r.flag && <span className="cs-flag">{r.flag}</span>}
                <span className="tnum">{r.main}</span>
                {r.sub && <div className="cs-dim mono">{r.sub}</div>}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr><td style={{ fontWeight: 800 }}>Total</td>
            <td className="tnum" style={{ textAlign: "right", fontWeight: 800 }}>{Math.round(calc.LF)} ft</td>
            <td style={{ textAlign: "right" }} className="cs-dim">{graded ? "⚠ graded runs — expect stepped/racked panels" : "all level"}</td></tr>
        </tfoot>
      </table>

      {/* build sequence — the heart of the sheet */}
      {/* before onsite — shop-prep checklist (compliance gates + steel-post depth per the install guide) */}
      <div className="cs-kicker">Before onsite{only ? "" : ` — ${name}`} <span className="cs-dim" style={{ textTransform: "none", letterSpacing: 0 }}>· shop prep · before you load up</span></div>
      <div className="cs-steps">
        {[
          ["Call 811 / Diggers Hotline", "verify utilities before pounding posts"],
          ["Confirm property lines", "keep the fence line ≥ 3″ inside the property"],
          ...(spec.category === "Vinyl" ? [["Mark steel-post depth", "permanent marker ~3.5′ (3–4′ range) — leaves ~4.5′ above grade for the 5×5 sleeve"]] : []),
          ["Snap a string line", "≥ 3″ within the property — keeps runs straight"],
          ["Confirm material delivery", "most materials ship direct to the customer — verify it landed"],
        ].map(([t, d]: any, i: number) => (
          <div key={i} className="cs-step">
            <span className="cs-cbx" aria-hidden="true" />
            <span className="cs-stepnum mono">•</span>
            <div className="cs-step-body"><div className="cs-step-head"><b>{t}</b><span className="cs-step-spec mono">{d}</span></div></div>
          </div>
        ))}
      </div>

      <div className="cs-kicker">Build sequence{only ? "" : ` — ${name}`} <span className="cs-dim" style={{ textTransform: "none", letterSpacing: 0 }}>· work in this order · check off as you go</span>
        {showTimes && sectionMin > 0 && <span className="mono" style={{ textTransform: "none", letterSpacing: 0, marginLeft: 8, fontWeight: 800, color: "#374151" }} title="Estimated install time — base rates, refine with tracked actuals">· Est. {only ? "job" : "section"} time: {fmtDur(sectionMin)}</span>}
      </div>
      <div className="cs-steps">
        {steps.map((st: any) => {
          const q = qtyLabel(st, counts);
          const mins = showTimes ? stepMinutes(st, counts, secRates, feet) : 0;
          return (
            <div key={st.n} className="cs-step">
              <span className="cs-cbx" aria-hidden="true" />
              <span className="cs-stepnum mono">{String(st.n).padStart(2, "0")}</span>
              <div className="cs-step-body">
                <div className="cs-step-head">
                  <b>{st.title}</b>
                  {q && <span className="cs-qty mono">{q}</span>}
                  {showTimes && mins > 0 && <span className="mono" style={{ fontSize: 11, fontWeight: 700, color: "#374151", whiteSpace: "nowrap" }} title="Estimated time for this step">· ~{fmtDur(mins)}</span>}
                  <span className="cs-step-spec mono">{st.spec}</span>
                </div>
                {st.note && <div className="cs-step-note">{st.note}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* CrewBuildSheet — print-optimized overlay: preview on screen, and
   window.print() prints ONLY the sheet (app chrome + the page behind it stay
   .no-print / hidden via the cs-overlay print rules in screens.css) */
function FenceBuildSheet({ quote: q, store, settings, onClose }: any) {
  const sIn = settings || (store && store.settings) || {};
  const view = computeQuoteView(q, sIn);
  const { product, fenceQuotes, quote } = view;
  const wo = ((store && store.workOrders) || []).find((w: any) => w.quoteId === q.id);
  const crew = wo ? ((sIn.crews || []).find((c: any) => c.id === wo.crewId)) : null;
  const company = (store && store.company) || {};
  React.useEffect(() => {
    const f = (e: any) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", f);
    return () => window.removeEventListener("keydown", f);
  }, [onClose]);

  const sections = fenceQuotes
    ? fenceQuotes.map((f: any) => ({ name: f.name, prod: f.prod, calc: f.q, inputs: f.snap }))
    : [{ name: null, prod: product, calc: quote, inputs: view.inputs }];
  // build-step time estimate: toggle + tweakable per-unit rates live on the
  // flexible settings object (defaults when unset). Off → the sheet is byte-for-
  // byte today's. Grand total = sum across every section's build steps, each
  // resolved with its product's sparse overrides + its footage (size scaling).
  const showTimes = !!sIn.showStepTimes;
  const rates = ratesWithDefaults(sIn.stepRates);
  const grandMin = showTimes
    ? sections.reduce((sum: number, sec: any) => {
        const { steps, counts, feet } = sectionSteps(sec);
        const secRates = ratesWithDefaults(sIn.stepRates, sec.prod && sec.prod.stepRates);
        return sum + totalMinutes(steps, counts, secRates, feet);
      }, 0)
    : 0;
  // ONE truck list for the whole job — same rows the quote/invoice BOM shows
  const bom = quoteBom(quote, product);
  const fmtD = (s: any) => { if (!s) return ""; const d = new Date(s + "T00:00:00"); return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }); };
  const sched = wo && wo.scheduledStart
    ? fmtD(wo.scheduledStart) + (wo.scheduledEnd && wo.scheduledEnd !== wo.scheduledStart ? " – " + fmtD(wo.scheduledEnd) : "")
    : null;

  return ReactDOM.createPortal(
    <div className="cs-overlay" data-dialog onClick={onClose}>
      <div className="cs-modal" onClick={(e: any) => e.stopPropagation()}>
        <div className="cs-bar no-print">
          <span className="row gap-8" style={{ fontSize: 13, fontWeight: 700 }}><Icon name="clipboard" size={14} className="muted" />Crew build sheet</span>
          <div className="row gap-8">
            <button className="btn btn-primary btn-sm" onClick={() => window.print()}><Icon name="printer" size={14} />Print / PDF</button>
            <button className="btn btn-ghost btn-sm" onClick={onClose}><Icon name="x" size={14} /></button>
          </div>
        </div>
        <div className="cs-scroll">
          <div className="crew-sheet">
            {/* header */}
            <div className="cs-head">
              <div className="row gap-12">
                <CompanyLogo company={company} size={40} />
                <div>
                  <div className="display" style={{ fontSize: 19 }}>{company.name || sIn.companyName || "Your Company"}</div>
                  <div className="cs-dim mono">{[company.phone, company.address].filter(Boolean).join(" · ")}</div>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="display" style={{ fontSize: 21, letterSpacing: ".04em" }}>BUILD SHEET</div>
                <div className="mono" style={{ fontSize: 12, marginTop: 3 }}>Job #{String(q.id || "").slice(-6).toUpperCase()} · {new Date().toLocaleDateString()}</div>
              </div>
            </div>

            {/* job / crew strip */}
            <div className="cs-job">
              <div>
                <div className="cs-kicker">Customer / Job site</div>
                <div style={{ fontWeight: 800, fontSize: 14 }}>{q.customer || "Customer"}</div>
                <div className="cs-dim">{q.address || "Address on work order"}</div>
                {q.phone && <div className="cs-dim mono">{q.phone}</div>}
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="cs-kicker">Crew</div>
                <div style={{ fontWeight: 800, fontSize: 14 }}>{crew ? crew.name + (crew.foreman ? " · " + crew.foreman : "") : "Unassigned"}</div>
                <div className="cs-dim">{sched ? "Scheduled " + sched : "Not scheduled yet"}{wo && wo.crewHours ? ` · ${wo.crewHours} crew-hrs est.` : ""}</div>
                {showTimes && grandMin > 0 && <div className="cs-dim mono" style={{ marginTop: 2 }}>Est. job time: <b style={{ color: "#374151" }}>{fmtDur(grandMin)}</b>{sections.length > 1 ? " · all sections" : ""}</div>}
              </div>
            </div>

            {sections.map((sec: any, i: number) => (
              <SheetSection key={i} {...sec} only={sections.length === 1} showTimes={showTimes} rates={rates} />
            ))}

            {/* bill of materials — what goes on the truck */}
            <div className="cs-kicker">Bill of materials <span className="cs-dim" style={{ textTransform: "none", letterSpacing: 0 }}>· load the truck — quantities match the signed estimate</span></div>
            <table className="cs-table cs-bom">
              <thead><tr><th style={{ width: 26 }}></th><th>Item</th><th style={{ textAlign: "right" }}>Qty</th></tr></thead>
              <tbody>
                {bom.map((r: any, k: number) => (
                  <tr key={k}>
                    <td><span className="cs-cbx sm" aria-hidden="true" /></td>
                    <td style={{ fontWeight: 600 }}>{r.item}</td>
                    <td className="tnum" style={{ textAlign: "right", fontWeight: 700 }}>{r.qty.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="cs-dim" style={{ margin: "6px 0 0" }}>Fasteners, caps &amp; consumables included. Exact counts may be field-verified at install.</p>

            {q.inputs && q.inputs.quoteNotes && (
              <><div className="cs-kicker">Notes</div>
                <p style={{ margin: 0, fontSize: 12.5, whiteSpace: "pre-wrap" }}>{q.inputs.quoteNotes}</p></>
            )}

            {/* footer */}
            <div className="cs-foot">
              <b>Measure twice — set once.</b> Verify final footage, gate locations &amp; grade on site before setting posts — lengths are field-verified at install. Confirm 811 utility marks before digging.
              <div className="cs-sign">
                <div className="cs-sign-col"><div className="cs-sign-rule"></div><div className="cs-sign-lbl">Foreman — footage verified on site</div></div>
                <div className="cs-sign-col"><div className="cs-sign-rule"></div><div className="cs-sign-lbl">Date</div></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>, document.body);
}

/* the sheet for the quote's service: the fence build sheet above, or the
   concrete crew sheet (concretecrew.tsx) — same overlay, same print rules */
function CrewBuildSheet(props: any) {
  return verticalOf(props.quote) === "concrete" ? <ConcreteCrewSheet {...props} /> : <FenceBuildSheet {...props} />;
}

Object.assign(window, { CrewBuildSheet });

export { CrewBuildSheet };
